import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

import { SURVEY_QUESTION_MOTION } from '../surveyQuestionMotion';

import { usePrefersReducedMotion } from '@/hooks/utils/usePrefersReducedMotion';

const FORWARD = 1;
const BACKWARD = -1;

type Direction = typeof FORWARD | typeof BACKWARD;

interface SurveyQuestionMoves {
    onNext: () => void;
    onPrevious: () => void;
    onSkip: () => void;
    onJump: (index: number) => void;
}

interface UseSurveyQuestionMotionOptions extends SurveyQuestionMoves {
    /** Index of the question on screen; a change is what replays the incoming half. */
    currentIndex: number;
}

export interface SurveyQuestionMotion {
    /** Goes on the question body, which also carries `key={currentIndex}`. */
    className: string;
    /** Carries the direction and the durations to the animation. */
    style: CSSProperties;
    next: () => void;
    previous: () => void;
    skip: () => void;
    jumpTo: (index: number) => void;
    /**
     * Arms the move that follows a pick. Calling it again restarts the countdown, so changing
     * one's mind — or dragging a scale — never stacks two moves.
     */
    confirmAnswer: () => void;
    /** Drops a move armed by `confirmAnswer`, for when the manager turns to a note instead. */
    cancelPendingAnswer: () => void;
}

/**
 * Sequences the survey's move from one question to the next: the picked answer holds the
 * spotlight, the question slides away, the parent changes index, the new question slides in from
 * the side the manager is heading towards. Every duration comes from `SURVEY_QUESTION_MOTION`.
 */
export const useSurveyQuestionMotion = ({
    currentIndex,
    onNext,
    onPrevious,
    onSkip,
    onJump,
}: UseSurveyQuestionMotionOptions): SurveyQuestionMotion => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [isLeaving, setIsLeaving] = useState(false);
    const [direction, setDirection] = useState<Direction>(FORWARD);
    // The first question is simply there when the panel opens; nothing slides until the manager
    // has asked the survey to move.
    const [hasMoved, setHasMoved] = useState(false);

    const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The move runs after a delay, by which time the parent has rebuilt its handlers around the
    // answer that was just given — so it is read at firing time, never captured when armed.
    const moves = useRef<SurveyQuestionMoves>({ onNext, onPrevious, onSkip, onJump });

    useEffect(() => {
        moves.current = { onNext, onPrevious, onSkip, onJump };
    }, [onNext, onPrevious, onSkip, onJump]);

    const { confirmMs, leaveMs, enterMs, travelPx } = SURVEY_QUESTION_MOTION;
    const leaveDuration = prefersReducedMotion ? 0 : leaveMs;
    const enterDuration = prefersReducedMotion ? 0 : enterMs;

    const cancelPendingAnswer = useCallback(() => {
        if (confirmTimer.current) {
            clearTimeout(confirmTimer.current);
            confirmTimer.current = null;
        }
    }, []);

    useEffect(
        () => () => {
            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }
            if (leaveTimer.current) {
                clearTimeout(leaveTimer.current);
            }
        },
        [],
    );

    const move = useCallback(
        (nextDirection: Direction, perform: (moves: SurveyQuestionMoves) => void) => {
            // A move is already under way: a second click must not skip a question.
            if (leaveTimer.current) {
                return;
            }
            cancelPendingAnswer();
            setDirection(nextDirection);
            setHasMoved(true);
            setIsLeaving(true);
            leaveTimer.current = setTimeout(() => {
                leaveTimer.current = null;
                setIsLeaving(false);
                perform(moves.current);
            }, leaveDuration);
        },
        [cancelPendingAnswer, leaveDuration],
    );

    const next = useCallback(() => move(FORWARD, (m) => m.onNext()), [move]);
    const previous = useCallback(() => move(BACKWARD, (m) => m.onPrevious()), [move]);
    const skip = useCallback(() => move(FORWARD, (m) => m.onSkip()), [move]);

    const jumpTo = useCallback(
        (index: number) => {
            if (index === currentIndex) {
                return;
            }
            move(index > currentIndex ? FORWARD : BACKWARD, (m) => m.onJump(index));
        },
        [currentIndex, move],
    );

    const confirmAnswer = useCallback(() => {
        cancelPendingAnswer();
        confirmTimer.current = setTimeout(() => {
            confirmTimer.current = null;
            next();
        }, confirmMs);
    }, [cancelPendingAnswer, confirmMs, next]);

    const resolveClassName = (): string => {
        if (isLeaving) {
            return 'survey-question-leave';
        }
        if (hasMoved) {
            return 'survey-question-enter';
        }
        return '';
    };

    return {
        className: resolveClassName(),
        style: {
            '--survey-question-travel': `${direction * travelPx}px`,
            '--survey-question-enter-duration': `${enterDuration}ms`,
            '--survey-question-leave-duration': `${leaveDuration}ms`,
        } as CSSProperties,
        next,
        previous,
        skip,
        jumpTo,
        confirmAnswer,
        cancelPendingAnswer,
    };
};
